import { ChartEntry } from '../types';

// Helper function to extract text between asterisks and clean the remaining text
const cleanAnnotations = (text: string): { cleanedText: string; message: string } => {
  const messageMatch = text.match(/\*(.*?)\*/);
  const message = messageMatch ? messageMatch[1].trim() : '';
  const cleanedText = text.replace(/\*.*?\*/g, '').trim();
  return { cleanedText, message };
};

// Helper function to check if a string is numeric
const isNumeric = (str: string): boolean => {
  if (typeof str !== 'string') return false; // we only process strings!
  return !isNaN(parseFloat(str)) && isFinite(Number(str));
};

export const processChartText = (text: string): ChartEntry[] => {
  const entries: ChartEntry[] = [];
  const lines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0);

  if (lines.length === 0) {
      console.warn("[processChartText] Input text is empty or contains only whitespace.");
      return [];
  }

  // --- Format Detection --- 
  const headerLines = lines.slice(0, 6).map(l => l.toLowerCase());
  const expectedHeaders = ['tw', 'lw', 'peak', 'weeks', 'song', 'artist'];
  const isNewVerticalMultiLineFormat = 
    headerLines.length === 6 &&
    headerLines.every((line, index) => line === expectedHeaders[index]);
  
  const isOldHorizontalFormat = lines[0]?.toUpperCase().startsWith('TW\tLW') || lines[0]?.toUpperCase().startsWith('TW LW');

  // --- Parsing Logic --- 

  if (isNewVerticalMultiLineFormat) {
    console.log("[processChartText] Detected NEW Vertical Headers / Multi-Line Entry format.");
    // Start processing after the 6 header lines, stepping by 6 (TW, LW, Pk, Wk, Song, Artist)
    for (let i = 6; i < lines.length; i += 6) { // STEP BY 6
        const twLine = lines[i];
        const lwLine = lines[i + 1];
        const peakLine = lines[i + 2];
        const weeksLine = lines[i + 3];
        const songLine = lines[i + 4];
        const artistLine = lines[i + 5];

        // Basic validation: ensure all six lines exist for the entry
        if (
            twLine === undefined || lwLine === undefined || peakLine === undefined || 
            weeksLine === undefined || songLine === undefined || artistLine === undefined
        ) {
            console.warn(`[processChartText - New Vertical] Skipping entry starting at index ${i}: Missing expected 6 lines.`);
            continue; // Skip this incomplete entry group
        }

        // 1. Parse individual data lines
        if (!isNumeric(twLine)) {
             console.warn(`[processChartText - New Vertical] Skipping entry at index ${i}: TW line is not numeric. Line: "${twLine}"`);
             continue;
        }
        
        try {
            const position = parseInt(twLine, 10);
            const lastWeek = isNumeric(lwLine) ? parseInt(lwLine, 10) : 0; // Only case where we use a default
            const peakStr = peakLine;
            const weeksStr = weeksLine;
            const peak = parseInt(peakStr, 10);
            const weeks = parseInt(weeksStr, 10);

            // 2. Parse Song Line (Clean annotations and extract message)
            const { cleanedText: song, message } = cleanAnnotations(songLine);

            // 3. Parse Artist Line
            const artist = artistLine.trim(); // Artist is the whole line

            // 4. Validate and Push Entry
            if (!isNaN(position) && song && artist && !isNaN(peak) && !isNaN(weeks)) {
                entries.push({
                    position,
                    lastWeek,
                    song,
                    artist,
                    peak,
                    weeks,
                    message: message || undefined,
                });
            } else {
                console.warn(`[processChartText - New Vertical] Skipping entry at index ${i}: Failed to parse required fields. Pos: ${position}, Peak: ${peak}, Weeks: ${weeks}, Song: ${song}, Artist: ${artist}`);
            }
        } catch (e) {
            console.error(`[processChartText - New Vertical] Error processing entry at index ${i}:`, e);
            // Log the individual lines for easier debugging
            console.error(`  TW Line: "${twLine}"`);
            console.error(`  LW Line: "${lwLine}"`);
            console.error(`  Peak Line: "${peakLine}"`);
            console.error(`  Weeks Line: "${weeksLine}"`);
            console.error(`  Song Line: "${songLine}"`);
            console.error(`  Artist Line: "${artistLine}"`);
        }
    }

  } else if (isOldHorizontalFormat) {
    console.log("[processChartText] Detected OLD Horizontal (Single Line) format.");
    for (let i = 1; i < lines.length; i++) { // Start from index 1 to skip header
      const originalLine = lines[i];
      const { cleanedText: cleanedLine, message } = cleanAnnotations(originalLine);
      const columns = cleanedLine.split(/\s{2,}|\t/).map(col => col.trim()).filter(col => col.length > 0);
      
      console.log(`[processChartText - Old Horizontal] Processing line ${i}: "${originalLine}" -> Cleaned: "${cleanedLine}"`);
      console.log(`[processChartText - Old Horizontal] Split columns:`, columns);

      if (columns.length >= 5 && isNumeric(columns[0])) {
        try {
          const position = parseInt(columns[0], 10);
          const lastWeek = isNumeric(columns[1]) ? parseInt(columns[1], 10) : 0;
          const peakStr = columns[2];
          const weeksStr = columns[3];
          const peak = parseInt(peakStr, 10);
          const weeks = parseInt(weeksStr, 10);
          
          let song = '';
          let artist = '';
          
          if (columns.length >= 6) {
            song = columns[4]; 
            artist = columns.slice(5).join(' '); 
          } else { // columns.length === 5
            song = columns[4];
            artist = ''; 
            console.warn(`[processChartText - Old Horizontal] Line ${i}: Only 5 columns detected, assuming artist is missing.`);
          }
          
          song = song.trim(); 
          artist = artist.trim(); 

          if (!isNaN(position) && song && artist && !isNaN(peak) && !isNaN(weeks)) {
            entries.push({
              position,
              lastWeek,
              song,
              artist,
              peak,
              weeks,
              message: message || undefined,
            });
          } else {
              console.warn(`[processChartText - Old Horizontal] Skipping invalid line ${i}: Failed parse. Pos: ${position}, Peak: ${peak}, Weeks: ${weeks}, Song: ${song}, Artist: ${artist}`);
          }
        } catch (e) {
          console.warn(`[processChartText - Old Horizontal] Skipping line ${i}: Error parsing. Error: ${e}, Line: ${originalLine}`);
        }
      } else {
         console.warn(`[processChartText - Old Horizontal] Skipping line ${i}: Invalid columns/format. Cols: ${columns.length}, Line: "${cleanedLine}"`);
      }
    }
  } else {
    console.log("[processChartText] Assuming ORIGINAL Vertical (6 lines per entry) format.");
    let lineIndex = 0; // Track overall line number for logging
    
    // Process in chunks of 6 lines (Pos, LW, Pk, Wks, Song, Artist)
    for (let i = 0; i < lines.length; i += 6) {
        const chunk = lines.slice(i, i + 6);
        lineIndex = i;

        if (chunk.length !== 6) {
            console.warn(`[processChartText - Original Vertical] Skipping entry starting at line ${lineIndex}: Incomplete chunk of ${chunk.length} lines.`);
            continue;
        }

        try {
             const positionStr = chunk[0];
             const lastWeekStr = chunk[1];
             const peakStr = chunk[2];
             const weeksStr = chunk[3];
             const songStr = chunk[4];
             const artistStr = chunk[5];

             // Parse values, without defaulting
             const position = parseInt(positionStr, 10);
             const lastWeek = isNumeric(lastWeekStr) ? parseInt(lastWeekStr, 10) : 0; // Only case where we use a default
             const peak = parseInt(peakStr, 10);
             const weeks = parseInt(weeksStr, 10);
             const { cleanedText: song, message } = cleanAnnotations(songStr);
             const artist = artistStr.trim();

            // Validation to ensure all required values are parsed correctly
            if (!isNaN(position) && song && artist && !isNaN(peak) && !isNaN(weeks)) {
                entries.push({
                    position,
                    lastWeek,
                    song,
                    artist,
                    peak,
                    weeks,
                    message: message || undefined,
                });
            } else {
                 console.warn(`[processChartText - Original Vertical] Skipping entry at line ${lineIndex}: Invalid data. Pos: ${position}, Peak: ${peak}, Weeks: ${weeks}, Song: ${song}, Artist: ${artist}`);
            }
        } catch (e) {
             console.error(`[processChartText - Original Vertical] Error processing entry at line ${lineIndex}:`, e);
             console.error(`   Chunk:`, chunk);
        }
    }
  }

  console.log("[processChartText] Parsed entries:", entries);
  return entries;
}; 